/**
 * Main App Component
 * Phase 10: Routing & App Setup
 * 
 * Defines all routes with proper guards and lazy loading
 */

import { Suspense, lazy } from 'react'
import { Routes, Route } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { ErrorBoundary } from '@/components/common/error-boundary'
import { LoadingSpinner } from '@/components/common/loading-spinner'

// Route Guards
import { RequireAuth } from '@/components/router/require-auth'
import { RequireAdmin } from '@/components/router/require-admin'

// Layout (not lazy-loaded for faster initial render)
import MainLayout from '@/pages/layout'

// Eager-loaded pages (critical for first paint)
import LoginPage from '@/pages/login'
import SignupPage from '@/pages/signup'
import ResetPasswordPage from '@/pages/reset-password'

// Lazy-loaded pages (code splitting)
const ListingsPage = lazy(() => import('@/pages/listings'))
const ListingDetailPage = lazy(() => import('@/pages/listings/[id]'))
const LeadsPage = lazy(() => import('@/pages/leads'))
const LeadDetailPage = lazy(() => import('@/pages/leads/[id]'))
const JobsPage = lazy(() => import('@/pages/jobs'))
const JobDetailPage = lazy(() => import('@/pages/jobs/[id]'))
const ProfilesPage = lazy(() => import('@/pages/profiles'))
const SystemPage = lazy(() => import('@/pages/system'))
const SettingsPage = lazy(() => import('@/pages/settings'))
const NotFoundPage = lazy(() => import('@/pages/not-found'))

// Page Loading Fallback
function PageLoader() {
  return (
    <div className={cn(
      'flex items-center justify-center',
      'min-h-[calc(100vh-64px)]' // Account for header
    )}>
      <LoadingSpinner size="lg" text="Loading page..." />
    </div>
  )
}

// Lazy Page Wrapper with Error Boundary
function LazyPage({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        {children}
      </Suspense>
    </ErrorBoundary>
  )
}

function App() {
  return (
    <div className="min-h-screen bg-[#ffffff]">
      <Routes>
        {/* Public Routes - No Auth Required */}
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Protected Routes - Auth Required */}
        <Route element={<RequireAuth />}>
          <Route element={<MainLayout />}>
            {/* Listings workspace home */}
            <Route 
              path="/" 
              element={
                <LazyPage>
                  <ListingsPage />
                </LazyPage>
              } 
            />

            {/* Listings */}
            <Route 
              path="/listings" 
              element={
                <LazyPage>
                  <ListingsPage />
                </LazyPage>
              } 
            />
            <Route 
              path="/listings/:id" 
              element={
                <LazyPage>
                  <ListingDetailPage />
                </LazyPage>
              } 
            />

            {/* Leads */}
            <Route 
              path="/leads" 
              element={
                <LazyPage>
                  <LeadsPage />
                </LazyPage>
              } 
            />
            <Route 
              path="/leads/:id" 
              element={
                <LazyPage>
                  <LeadDetailPage />
                </LazyPage>
              } 
            />

            {/* Jobs */}
            <Route 
              path="/jobs" 
              element={
                <LazyPage>
                  <JobsPage />
                </LazyPage>
              } 
            />
            <Route 
              path="/jobs/:id" 
              element={
                <LazyPage>
                  <JobDetailPage />
                </LazyPage>
              } 
            />

            {/* Profiles */}
            <Route 
              path="/profiles" 
              element={
                <LazyPage>
                  <ProfilesPage />
                </LazyPage>
              } 
            />

            {/* Settings */}
            <Route 
              path="/settings" 
              element={
                <LazyPage>
                  <SettingsPage />
                </LazyPage>
              } 
            />
          </Route>
        </Route>

        {/* Admin-Only Routes */}
        <Route element={<RequireAuth />}>
          <Route element={<RequireAdmin />}>
            <Route element={<MainLayout requireAdmin />}>
              <Route 
                path="/system" 
                element={
                  <LazyPage>
                    <SystemPage />
                  </LazyPage>
                } 
              />
            </Route>
          </Route>
        </Route>

        {/* 404 Not Found */}
        <Route 
          path="*" 
          element={
            <LazyPage>
              <NotFoundPage />
            </LazyPage>
          } 
        />
      </Routes>
    </div>
  )
}

export default App
